import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Bell, AlertTriangle, CheckCircle, Clock, Plus, ArrowLeft, Shield,
  Zap, Activity, Phone, FileText, Eye
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomAlertsDashboard() {
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [templates, setTemplates] = useState({});
  const [triageTriggers, setTriageTriggers] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTriageDialog, setShowTriageDialog] = useState(false);
  
  const [newAlert, setNewAlert] = useState({
    title: "",
    message: "",
    category: "custom",
    priority: "medium",
    patient_id: "",
    patient_name: "",
    recommended_actions: []
  });

  const [triageAssessment, setTriageAssessment] = useState({
    patient_id: "",
    patient_name: "",
    documentation_text: "",
    call_reason: "",
    caller_type: "family"
  });

  const [triageResult, setTriageResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [alertsRes, templatesRes, triggersRes, dashboardRes] = await Promise.all([
        fetch(`${API_URL}/api/custom-alerts/active`),
        fetch(`${API_URL}/api/custom-alerts/templates`),
        fetch(`${API_URL}/api/custom-alerts/triage/triggers`),
        fetch(`${API_URL}/api/custom-alerts/dashboard`)
      ]);
      
      const alertsData = await alertsRes.json();
      const templatesData = await templatesRes.json();
      const triggersData = await triggersRes.json();
      const dashboardData = await dashboardRes.json();
      
      setActiveAlerts(alertsData.alerts || []);
      setTemplates(templatesData.templates || {});
      setTriageTriggers(triggersData.triggers || {});
      setDashboard(dashboardData);
    } catch (err) {
      console.error("Failed to load data:", err);
    }
  };

  const createAlert = async () => {
    if (!newAlert.title || !newAlert.message) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/custom-alerts/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...newAlert,
          created_by_id: "staff_001",
          created_by_name: "Current User"
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Alert created: ${data.alert_id}`);
        setShowCreateDialog(false);
        setNewAlert({
          title: "",
          message: "",
          category: "custom",
          priority: "medium",
          patient_id: "",
          patient_name: "",
          recommended_actions: []
        });
        loadData();
      }
    } catch (err) {
      toast.error("Failed to create alert");
    }
    setLoading(false);
  };

  const createFromTemplate = async (templateKey) => {
    try {
      const res = await fetch(`${API_URL}/api/custom-alerts/from-template/${templateKey}?patient_name=Demo Patient`, {
        method: "POST"
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Alert created from template: ${data.alert_id}`);
        loadData();
      }
    } catch (err) {
      toast.error("Failed to create alert");
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/custom-alerts/alert/${alertId}/acknowledge?user_id=staff_001&user_name=Current User`, {
        method: "POST"
      });
      
      if (res.ok) {
        toast.success("Alert acknowledged");
        loadData();
      }
    } catch (err) {
      toast.error("Failed to acknowledge alert");
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/custom-alerts/alert/${alertId}/resolve?user_id=staff_001&user_name=Current User&resolution_notes=Resolved via dashboard`, {
        method: "POST"
      });
      
      if (res.ok) {
        toast.success("Alert resolved");
        loadData();
      }
    } catch (err) {
      toast.error("Failed to resolve alert");
    }
  };

  const runTriageAssessment = async () => {
    if (!triageAssessment.patient_name || !triageAssessment.documentation_text) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/custom-alerts/triage/assess`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: triageAssessment.patient_id || `pt_${Date.now()}`,
          patient_name: triageAssessment.patient_name,
          documentation_text: triageAssessment.documentation_text,
          call_reason: triageAssessment.call_reason,
          caller_type: triageAssessment.caller_type
        })
      });
      
      const data = await res.json();
      setTriageResult(data);
      
      if (data.risk_level === "critical" || data.risk_level === "high") {
        toast.warning(`${data.risk_level.toUpperCase()} risk detected - Alert created automatically`);
      } else {
        toast.success(`Triage complete: ${data.risk_level} risk`);
      }
      
      loadData();
    } catch (err) {
      toast.error("Failed to run triage assessment");
    }
    setLoading(false);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical": return "bg-red-500";
      case "high": return "bg-orange-500";
      case "medium": return "bg-yellow-500";
      case "low": return "bg-blue-500";
      default: return "bg-slate-500";
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case "critical": return "bg-red-500 text-white";
      case "high": return "bg-orange-500 text-white";
      case "moderate": return "bg-yellow-500 text-black";
      case "low": return "bg-green-500 text-white";
      default: return "bg-slate-500 text-white";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/hospice-ehr" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Custom Alerts & Triage</h1>
              <p className="text-slate-600">Create alerts and assess triage risk</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Dialog open={showTriageDialog} onOpenChange={setShowTriageDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="triage-btn">
                  <Activity className="w-4 h-4 mr-2" />
                  Triage Assessment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Triage Risk Assessment</DialogTitle>
                  <DialogDescription>
                    Assess triage risk based on documentation or call notes
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Patient Name *</Label>
                      <Input
                        value={triageAssessment.patient_name}
                        onChange={(e) => setTriageAssessment({...triageAssessment, patient_name: e.target.value})}
                        placeholder="Patient name"
                      />
                    </div>
                    <div>
                      <Label>Caller Type</Label>
                      <Select value={triageAssessment.caller_type} onValueChange={(v) => setTriageAssessment({...triageAssessment, caller_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="family">Family</SelectItem>
                          <SelectItem value="patient">Patient</SelectItem>
                          <SelectItem value="facility">Facility</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Call Reason</Label>
                    <Input
                      value={triageAssessment.call_reason}
                      onChange={(e) => setTriageAssessment({...triageAssessment, call_reason: e.target.value})}
                      placeholder="Brief reason for call"
                    />
                  </div>
                  <div>
                    <Label>Documentation / Notes *</Label>
                    <Textarea
                      value={triageAssessment.documentation_text}
                      onChange={(e) => setTriageAssessment({...triageAssessment, documentation_text: e.target.value})}
                      placeholder="Enter symptoms, concerns, or call notes. System will automatically detect risk triggers..."
                      rows={5}
                    />
                  </div>
                  
                  {triageResult && (
                    <div className={`p-4 rounded-lg ${
                      triageResult.risk_level === "critical" ? "bg-red-100 border border-red-300" :
                      triageResult.risk_level === "high" ? "bg-orange-100 border border-orange-300" :
                      triageResult.risk_level === "moderate" ? "bg-yellow-100 border border-yellow-300" :
                      "bg-green-100 border border-green-300"
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-lg">Triage Result</span>
                        <Badge className={getRiskColor(triageResult.risk_level)}>
                          {triageResult.risk_level.toUpperCase()} RISK
                        </Badge>
                      </div>
                      <p className="text-sm mb-2">
                        <strong>Response Time:</strong> {triageResult.recommended_response_time}
                      </p>
                      <p className="text-sm mb-2">
                        <strong>Risk Score:</strong> {(triageResult.risk_score * 100).toFixed(0)}%
                      </p>
                      {triageResult.risk_factors?.length > 0 && (
                        <div className="mb-2">
                          <strong className="text-sm">Triggers Found:</strong>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {triageResult.risk_factors.map((f, idx) => (
                              <Badge key={idx} variant="outline">{f}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {triageResult.recommended_actions?.length > 0 && (
                        <div>
                          <strong className="text-sm">Recommended Actions:</strong>
                          <ul className="list-disc list-inside text-sm mt-1">
                            {triageResult.recommended_actions.slice(0, 4).map((a, idx) => (
                              <li key={idx}>{a}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setShowTriageDialog(false);
                    setTriageResult(null);
                  }}>Close</Button>
                  <Button onClick={runTriageAssessment} disabled={loading}>
                    {loading ? "Assessing..." : "Run Assessment"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700" data-testid="create-alert-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Alert
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Custom Alert</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Title *</Label>
                    <Input
                      value={newAlert.title}
                      onChange={(e) => setNewAlert({...newAlert, title: e.target.value})}
                      placeholder="Alert title"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={newAlert.category} onValueChange={(v) => setNewAlert({...newAlert, category: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="clinical">Clinical</SelectItem>
                          <SelectItem value="operational">Operational</SelectItem>
                          <SelectItem value="compliance">Compliance</SelectItem>
                          <SelectItem value="communication">Communication</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={newAlert.priority} onValueChange={(v) => setNewAlert({...newAlert, priority: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="critical">Critical</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Patient Name (optional)</Label>
                    <Input
                      value={newAlert.patient_name}
                      onChange={(e) => setNewAlert({...newAlert, patient_name: e.target.value})}
                      placeholder="Related patient"
                    />
                  </div>
                  <div>
                    <Label>Message *</Label>
                    <Textarea
                      value={newAlert.message}
                      onChange={(e) => setNewAlert({...newAlert, message: e.target.value})}
                      placeholder="Alert message..."
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={createAlert} disabled={loading}>
                    {loading ? "Creating..." : "Create Alert"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Critical</p>
                  <p className="text-2xl font-bold text-red-900">{dashboard?.summary?.critical || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">High</p>
                  <p className="text-2xl font-bold text-orange-900">{dashboard?.summary?.high || 0}</p>
                </div>
                <Zap className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-600">Medium</p>
                  <p className="text-2xl font-bold text-yellow-900">{dashboard?.summary?.medium || 0}</p>
                </div>
                <Bell className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Active</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.summary?.total_active || 0}</p>
                </div>
                <Shield className="w-8 h-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border shadow-sm mb-6">
            <TabsTrigger value="active" data-testid="active-tab">
              Active Alerts
              {(dashboard?.summary?.total_active || 0) > 0 && (
                <Badge className="ml-2 bg-red-500">{dashboard?.summary?.total_active}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">Alert Templates</TabsTrigger>
            <TabsTrigger value="triage" data-testid="triage-triggers-tab">Triage Triggers</TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            <Card>
              <CardHeader>
                <CardTitle>Active Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                {activeAlerts.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500 opacity-50" />
                    <p>No active alerts</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeAlerts.map((alert, idx) => (
                      <div key={idx} className={`p-4 border rounded-lg ${
                        alert.priority === "critical" ? "border-red-300 bg-red-50" :
                        alert.priority === "high" ? "border-orange-300 bg-orange-50" :
                        "border-slate-200"
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <Badge className={getPriorityColor(alert.priority)}>
                              {alert.priority?.toUpperCase()}
                            </Badge>
                            <div>
                              <p className="font-semibold">{alert.title}</p>
                              <p className="text-sm text-slate-600">{alert.category}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => acknowledgeAlert(alert.alert_id)}>
                              Acknowledge
                            </Button>
                            <Button size="sm" onClick={() => resolveAlert(alert.alert_id)}>
                              Resolve
                            </Button>
                          </div>
                        </div>
                        <p className="mt-2">{alert.message}</p>
                        {alert.patient_name && (
                          <p className="text-sm text-slate-500 mt-1">Patient: {alert.patient_name}</p>
                        )}
                        {alert.recommended_actions?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-medium">Actions:</p>
                            <ul className="list-disc list-inside text-sm">
                              {alert.recommended_actions.slice(0, 3).map((a, aIdx) => (
                                <li key={aIdx}>{a}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <CardTitle>Alert Templates</CardTitle>
                <CardDescription>Quick-create alerts from predefined templates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(templates).map(([key, template]) => (
                    <div key={key} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{template.template_name}</span>
                        <Badge className={getPriorityColor(template.default_priority)}>
                          {template.default_priority}
                        </Badge>
                      </div>
                      <Badge variant="outline" className="mb-2">{template.category}</Badge>
                      <p className="text-sm text-slate-600 mb-3">{template.message_template?.slice(0, 80)}...</p>
                      <Button size="sm" variant="outline" onClick={() => createFromTemplate(key)}>
                        Use Template
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="triage">
            <Card>
              <CardHeader>
                <CardTitle>Triage Risk Triggers</CardTitle>
                <CardDescription>Keywords that automatically determine triage risk level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {Object.entries(triageTriggers).map(([level, data]) => (
                    <div key={level} className={`p-4 rounded-lg ${
                      level === "critical" ? "bg-red-50 border border-red-200" :
                      level === "high" ? "bg-orange-50 border border-orange-200" :
                      level === "moderate" ? "bg-yellow-50 border border-yellow-200" :
                      "bg-green-50 border border-green-200"
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={getRiskColor(level)}>{level.toUpperCase()}</Badge>
                        <span className="text-sm font-medium">{data.response_time}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {data.keywords?.map((keyword, idx) => (
                          <Badge key={idx} variant="outline">{keyword}</Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
